import {
  canAccessFeature,
  type CollaboratorRole,
  type Feature,
} from '../../services/auth/permissions';
import { resolveHidden } from '../../lib/tabVisibility';

export const DASHBOARD_VIEW_ID = 'dashboard';

export const WIDGET_SPAN_STEPS = [4, 6, 8, 12] as const;
export type WidgetColSpan = (typeof WIDGET_SPAN_STEPS)[number];

/** Grille du tableau de bord : 12 colonnes, rangées de 40 px, 24 px d'écart. */
export const DASHBOARD_GRID_COLS = 12;
export const DASHBOARD_ROW_HEIGHT = 40;
export const DASHBOARD_GRID_GAP = 24;
export const DASHBOARD_MAX_ROWS = 24;

/** Place d'un bloc dans la grille, en colonnes et en rangées. */
export interface WidgetBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardWidgetDef {
  key: string;
  label: string;
  defaultSpan: WidgetColSpan;
  minSpan: WidgetColSpan;
  /** Hauteur par défaut, en rangées de grille. */
  defaultRows: number;
  minRows: number;
  href?: string;
  feature?: Feature;
}

export const DASHBOARD_WIDGETS: DashboardWidgetDef[] = [
  { key: 'welcome', label: 'Message de bienvenue', defaultSpan: 12, minSpan: 8, defaultRows: 3, minRows: 3 },
  { key: 'kpis', label: 'Indicateurs du cabinet', defaultSpan: 12, minSpan: 8, defaultRows: 4, minRows: 3, feature: 'stats_cabinet_view' },
  { key: 'recent-dossiers', label: 'Dossiers récents', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/dossiers' },
  { key: 'parapheur', label: 'Parapheur', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/parapheur', feature: 'signature_my_parapheur' },
  { key: 'dossiers-kanban', label: 'Dossiers par étape', defaultSpan: 12, minSpan: 8, defaultRows: 6, minRows: 4, href: '/dossiers/kanban' },
  { key: 'agenda', label: 'Agenda', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/calendrier' },
  { key: 'ordre-du-jour', label: 'Ordre du jour', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/calendrier' },
  { key: 'my-tasks', label: 'Mes tâches', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/taches' },
  { key: 'tickets', label: 'Demandes', defaultSpan: 6, minSpan: 4, defaultRows: 8, minRows: 4, href: '/tickets' },
];

export const DASHBOARD_DEFAULT_HIDDEN = [
  'welcome',
  'kpis',
  'dossiers-kanban',
];

export function widgetsForRole(
  role: CollaboratorRole | null,
): DashboardWidgetDef[] {
  return DASHBOARD_WIDGETS.filter(
    (w) => !w.feature || (!!role && canAccessFeature(role, w.feature)),
  );
}

export function dashboardTabOptions(
  role: CollaboratorRole | null,
): { key: string; label: string }[] {
  return widgetsForRole(role).map((w) => ({ key: w.key, label: w.label }));
}

export function applyWidgetOrder<T extends { key: string }>(
  catalog: T[],
  stored: string[] | undefined,
): T[] {
  if (!stored || stored.length === 0) return catalog;
  const byKey = new Map(catalog.map((w) => [w.key, w]));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const key of stored) {
    const w = byKey.get(key);
    if (w && !seen.has(key)) {
      out.push(w);
      seen.add(key);
    }
  }
  for (const w of catalog) {
    if (!seen.has(w.key)) out.push(w);
  }
  return out;
}

export function spliceVisibleOrder(
  full: string[],
  newVisible: string[],
): string[] {
  const visSet = new Set(newVisible);
  let i = 0;
  return full.map((k) => (visSet.has(k) ? newVisible[i++]! : k));
}

export function clampWidgetSpan(
  value: number | undefined,
  minSpan: WidgetColSpan,
  fallback?: WidgetColSpan,
  maxSpan: WidgetColSpan = 12,
): WidgetColSpan {
  const allowed = WIDGET_SPAN_STEPS.filter((s) => s >= minSpan && s <= maxSpan);
  const pick = allowed[0] ?? minSpan;
  if (value == null || Number.isNaN(value)) {
    return fallback ?? pick;
  }
  let nearest = pick;
  let best = Infinity;
  for (const step of allowed) {
    const dist = Math.abs(step - value);
    if (dist < best) {
      best = dist;
      nearest = step;
    }
  }
  return nearest;
}

export function visibleDashboardWidgets(
  storedHidden: string[] | undefined,
  role: CollaboratorRole | null,
  storedOrder?: string[],
): DashboardWidgetDef[] {
  const hidden = resolveHidden(storedHidden, DASHBOARD_DEFAULT_HIDDEN);
  const ordered = applyWidgetOrder(widgetsForRole(role), storedOrder);
  return ordered.filter((w) => !hidden.includes(w.key));
}

/** Convertit une hauteur en pixels (ancienne disposition) en rangées de grille. */
export function rowsFromPx(px: number): number {
  return Math.max(1, Math.round((px + DASHBOARD_GRID_GAP) / (DASHBOARD_ROW_HEIGHT + DASHBOARD_GRID_GAP)));
}

function clampBox(
  box: WidgetBox,
  w: Pick<DashboardWidgetDef, 'minSpan' | 'minRows'>,
): WidgetBox {
  const width = Math.min(DASHBOARD_GRID_COLS, Math.max(w.minSpan, Math.round(box.w)));
  const height = Math.min(DASHBOARD_MAX_ROWS, Math.max(w.minRows, Math.round(box.h)));
  const x = Math.min(DASHBOARD_GRID_COLS - width, Math.max(0, Math.round(box.x)));
  return { x, y: Math.max(0, Math.round(box.y)), w: width, h: height };
}

/**
 * Place des blocs visibles. Un bloc déjà placé garde sa place. Les autres
 * (nouveau bloc, bloc réaffiché, ancienne disposition) sont rangés à la suite,
 * de gauche à droite, avec leur largeur et leur hauteur d'avant si elles existent.
 */
export function resolveDashboardLayout(
  widgets: Pick<DashboardWidgetDef, 'key' | 'defaultSpan' | 'minSpan' | 'defaultRows' | 'minRows'>[],
  stored?: Record<string, WidgetBox>,
  legacy?: { spans?: Record<string, number>; heights?: Record<string, number> },
): Record<string, WidgetBox> {
  const out: Record<string, WidgetBox> = {};
  let bottom = 0;
  for (const w of widgets) {
    const box = stored?.[w.key];
    if (!box) continue;
    const placed = clampBox(box, w);
    out[w.key] = placed;
    bottom = Math.max(bottom, placed.y + placed.h);
  }
  let x = 0;
  let y = bottom;
  let rowH = 0;
  for (const w of widgets) {
    if (out[w.key]) continue;
    const span = clampWidgetSpan(legacy?.spans?.[w.key], w.minSpan, w.defaultSpan);
    const px = legacy?.heights?.[w.key];
    const h = px == null || Number.isNaN(px) ? w.defaultRows : rowsFromPx(px);
    if (x + span > DASHBOARD_GRID_COLS) {
      x = 0;
      y += rowH;
      rowH = 0;
    }
    const placed = clampBox({ x, y, w: span, h }, w);
    out[w.key] = placed;
    x += placed.w;
    rowH = Math.max(rowH, placed.h);
  }
  return out;
}

/** Ordre de lecture (haut en bas, puis gauche à droite) : sert à l'affichage en une colonne. */
export function keysInReadingOrder(boxes: Record<string, WidgetBox>): string[] {
  return Object.entries(boxes)
    .sort(([, a], [, b]) => a.y - b.y || a.x - b.x)
    .map(([key]) => key);
}
